import { useMemo } from "react";
import { Alert, Anchor, Loader, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Locale } from "@iso27001/shared";
import { docsApi, type DocumentListItem } from "../../api/docs.js";
import { DocSearch } from "./DocSearch.js";

/**
 * Groups documents by section, preserving the documentation's own order.
 *
 * Groups consecutive runs of the same `sectionTitle` rather than grouping by
 * key identity: a new group starts whenever the current document's section
 * differs from the previous document's, so a section that recurs in more
 * than one non-contiguous run (e.g. two "no heading" topics separated by a
 * named section) stays in its true server-order position instead of being
 * merged into its first occurrence.
 */
function bySection(items: DocumentListItem[]): Array<{ title: string; docs: DocumentListItem[] }> {
  const groups: Array<{ title: string; docs: DocumentListItem[] }> = [];
  for (const d of items) {
    const key = d.sectionTitle ?? "";
    const current = groups[groups.length - 1];
    if (current && current.title === key) {
      current.docs.push(d);
    } else {
      groups.push({ title: key, docs: [d] });
    }
  }
  return groups;
}

export function DokumentationPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const { data, isLoading, error } = useQuery({
    queryKey: ["docs", locale],
    queryFn: () => docsApi.list(locale),
  });
  const sections = useMemo(() => bySection(data?.items ?? []), [data]);

  if (isLoading) return <Loader />;
  if (error) return <Alert color="red">{t("docs.list.loadError")}</Alert>;
  if (sections.length === 0) {
    return (
      <Stack>
        <Title order={2}>{t("docs.title")}</Title>
        <DocSearch />
        <Text c="dimmed">{t("docs.list.empty")}</Text>
      </Stack>
    );
  }

  return (
    <Stack>
      <Title order={2}>{t("docs.title")}</Title>
      <DocSearch />
      {sections.map((s) => (
        // Keyed by the group's first document slug (unique per document), not
        // by title: consecutive-run grouping can legitimately produce more
        // than one group with the same title (or the same "" no-heading key),
        // so a title-derived key would collide.
        <Stack key={s.docs[0]!.slug} gap={4}>
          {s.title && <Text fw={600} mt="md">{s.title}</Text>}
          {s.docs.map((d) => (
            <Anchor key={d.slug} component={Link} to={`/dokumentation/${d.slug}`}>
              {d.title}
            </Anchor>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
